import type {
  CampaignStatus,
  LeadStatus,
  PauseReason,
  ReplyCategory,
  ReviewBlocker,
  ReviewWarning,
  StopReason,
  Weekday,
} from "@amtana/campaign-contract";
import type { Tone } from "@amtana/design-system";
import type { Catalogs, Formats, PlainKey, Translate, Translation } from "@amtana/i18n/catalog";

// Words for the campaign list and new-campaign screens, and the labels every
// campaign screen shares, from the reference's copy. The detail screen keeps
// its words in campaign-detail-copy.ts, the wizard in campaign-wizard-copy.ts.

export type CampaignCopyKey = keyof typeof EN;
export type CampaignCatalog = typeof EN;

const EN = {
  // Screen titles and the sidebar entry (declared in campaign.web.ts)
  listScreenTitle: "Campaigns · {product}",
  newScreenTitle: "New campaign · {product}",
  editScreenTitle: "Edit campaign · {product}",
  detailScreenTitle: "Campaign · {product}",

  // Words every campaign screen shares
  home: "Home",
  campaigns: "Campaigns",
  campaign: "Campaign",
  untitledCampaign: "Untitled campaign",
  thisCampaign: "this campaign",
  couldNotLoad: "Could not load the campaign",
  edit: "Edit",
  pause: "Pause",
  resume: "Resume",
  delete: "Delete",
  status: "Status",
  allStatuses: "All statuses",
  leads: "Leads",
  sequence: "Sequence",
  settings: "Settings",
  mailboxes: "Mailboxes",
  account: "Account",
  dailyLimit: "Daily limit",
  inbox: "Inbox",
  connected: "Connected",
  off: "Off",
  incomplete: "Incomplete",
  sent: "Sent",
  opened: "Opened",
  clicked: "Clicked",
  replied: "Replied",
  bounced: "Bounced",
  delivered: "Delivered",
  deletedAccount: "Deleted account",
  emailNumber: "Email {number}",
  dayNumber: "Day {day}",
  openRate: "Open rate",
  createdToday: "Created today",
  createdDaysAgo: { one: "Created 1 day ago", other: "Created {count} days ago" },
  createdMonthsAgo: { one: "Created 1 month ago", other: "Created {count} months ago" },
  createdYearsAgo: { one: "Created a year ago", other: "Created {count} years ago" },

  // Launch, pause, resume and save, from the detail screen and the wizard
  campaignLaunched: "Campaign launched",
  campaignSavedNotStarted: "Campaign saved, not started yet",
  couldNotLaunch: "Could not launch the campaign",
  couldNotSave: "Could not save the campaign",
  campaignPaused: "Campaign paused",
  couldNotPause: "Could not pause the campaign",
  campaignResumed: "Campaign resumed",
  couldNotResume: "Could not resume the campaign",

  // Campaign statuses
  statusDraft: "Draft",
  statusNotStarted: "Not Started",
  statusRunning: "Running",
  statusPaused: "Paused",
  statusCompleted: "Completed",
  statusStopped: "Stopped",

  // Why a campaign paused
  pauseManual: "You paused this campaign.",
  pauseHighBounce:
    "Paused automatically: the bounce rate went above your protection threshold. Review the audience before resuming.",
  pauseSenderInvalid: "Paused automatically: a mailbox needs reconnecting.",
  pauseNoSenders: "Paused automatically: none of its mailboxes can send. Fix mailboxes to resume.",

  // Lead statuses, stop reasons and reply categories
  leadActive: "Active",
  leadWaiting: "Waiting",
  leadCompleted: "Completed",
  leadStopped: "Stopped",
  leadBounced: "Bounced",
  leadSkipped: "Skipped",
  leadReplied: "Replied",
  stopReplied: "Replied",
  stopClicked: "Clicked a link",
  stopOpened: "Opened the email",
  stopCompanyReplied: "Someone else at this company replied",
  stopBounced: "The address bounced",
  stopUnsubscribed: "Unsubscribed",
  stopDoNotContact: "Marked as Do Not Contact",
  replyInterested: "Interested",
  replyNotInterested: "Not interested",
  replyOutOfOffice: "Out of office",
  replyWrongPerson: "Wrong person",

  // What blocks a launch, and what only deserves a warning
  blockerName: "Add a campaign name",
  blockerSource: "Select a lead source",
  blockerAudience: "The selected source has no emails to send to",
  blockerSequence: "Add at least one email step",
  blockerEmptyBody: "Some email steps have an empty body",
  blockerFirstSubject: "The first email needs a subject",
  blockerMailboxes: "Select at least one sending mailbox",
  blockerTimezone: "Set a timezone in Schedule",
  blockerNoActiveDays: "Select at least one active sending day",
  warningNoTracking: "Open and click tracking are off, so you won't see engagement",
  warningSingleMailbox: "Only one mailbox selected. Add more to improve deliverability",
  warningNoStartDate: "No start date. The campaign starts as soon as you launch it",
  warningLongDuration: "Reaching the whole audience will take a long time at the current capacity",
  warningReplyTracking:
    "No selected mailbox has the unified inbox connected, so replies and stop-on-reply won't be tracked. Enable it on your email accounts.",
  warningStopOpenNeedsTracking:
    "You stop on open, but open tracking is off, so the stop will never fire. Enable “Track opens”.",
  warningStopClickNeedsTracking:
    "You stop on click, but click tracking is off, so the stop will never fire. Enable “Track clicks”.",

  // Weekdays
  weekdayMonday: "Mon",
  weekdayTuesday: "Tue",
  weekdayWednesday: "Wed",
  weekdayThursday: "Thu",
  weekdayFriday: "Fri",
  weekdaySaturday: "Sat",
  weekdaySunday: "Sun",

  // Tracking and reply categorization, shown on the detail settings and in the wizard
  smartCategorization: "Smart AI categorization",
  disabled: "Disabled",
  replyCategorization: "Reply categorization",
  trackOpens: "Track opens",
  trackClicks: "Track clicks",

  // New campaign screen
  couldNotCreate: "Could not create the campaign",
  upgradePlan: "Upgrade Plan",
  backToCampaigns: "Back to campaigns",
  creatingCampaign: "Creating your campaign…",

  // Campaign list screen
  listDescription: "Create, add or remove campaigns.",
  manageEmailAccounts: "Manage email accounts",
  newCampaign: "New campaign",
  showingResults: { one: "Showing 1 result", other: "Showing {count} results" },
  noCampaignsTitle: "No campaigns yet",
  noCampaignsDescription:
    "Write a sequence, pick your mailboxes and reach the leads from an analysis or a filter.",
  columnTitle: "Title",
  columnStats: "Stats",
  columnActions: "Actions",
  actionsFor: "Actions for {name}",
  viewStats: "View stats",
  continueEditing: "Continue editing",
  deleteTitle: "Delete {name}?",
  deleteDescription: "Its leads stay in your prospects. This action cannot be undone.",
  campaignDeleted: "Campaign deleted",
  couldNotDelete: "Could not delete the campaign",
} as const;

const ES: Translation<CampaignCatalog> = {
  listScreenTitle: "Campañas · {product}",
  newScreenTitle: "Nueva campaña · {product}",
  editScreenTitle: "Editar campaña · {product}",
  detailScreenTitle: "Campaña · {product}",

  home: "Inicio",
  campaigns: "Campañas",
  campaign: "Campaña",
  untitledCampaign: "Campaña sin título",
  thisCampaign: "esta campaña",
  couldNotLoad: "No se pudo cargar la campaña",
  edit: "Editar",
  pause: "Pausar",
  resume: "Reanudar",
  delete: "Eliminar",
  status: "Estado",
  allStatuses: "Todos los estados",
  leads: "Leads",
  sequence: "Secuencia",
  settings: "Ajustes",
  mailboxes: "Buzones",
  account: "Cuenta",
  dailyLimit: "Límite diario",
  inbox: "Bandeja",
  connected: "Conectada",
  off: "Desactivada",
  incomplete: "Incompleto",
  sent: "Enviados",
  opened: "Abiertos",
  clicked: "Clics",
  replied: "Respondidos",
  bounced: "Rebotados",
  delivered: "Entregado",
  deletedAccount: "Cuenta eliminada",
  emailNumber: "Correo {number}",
  dayNumber: "Día {day}",
  openRate: "Tasa de apertura",
  createdToday: "Creada hoy",
  createdDaysAgo: { one: "Creada hace 1 día", other: "Creada hace {count} días" },
  createdMonthsAgo: { one: "Creada hace 1 mes", other: "Creada hace {count} meses" },
  createdYearsAgo: { one: "Creada hace un año", other: "Creada hace {count} años" },

  campaignLaunched: "Campaña lanzada",
  campaignSavedNotStarted: "Campaña guardada, aún sin iniciar",
  couldNotLaunch: "No se pudo lanzar la campaña",
  couldNotSave: "No se pudo guardar la campaña",
  campaignPaused: "Campaña pausada",
  couldNotPause: "No se pudo pausar la campaña",
  campaignResumed: "Campaña reanudada",
  couldNotResume: "No se pudo reanudar la campaña",

  statusDraft: "Borrador",
  statusNotStarted: "Sin iniciar",
  statusRunning: "En curso",
  statusPaused: "En pausa",
  statusCompleted: "Completada",
  statusStopped: "Detenida",

  pauseManual: "Pausaste esta campaña.",
  pauseHighBounce:
    "Pausada automáticamente: la tasa de rebote superó tu umbral de protección. Revisa la audiencia antes de reanudar.",
  pauseSenderInvalid: "Pausada automáticamente: hay que reconectar un buzón.",
  pauseNoSenders:
    "Pausada automáticamente: ninguno de sus buzones puede enviar. Arregla los buzones para reanudar.",

  leadActive: "Activo",
  leadWaiting: "En espera",
  leadCompleted: "Completado",
  leadStopped: "Detenido",
  leadBounced: "Rebotado",
  leadSkipped: "Omitido",
  leadReplied: "Respondió",
  stopReplied: "Respondió",
  stopClicked: "Hizo clic en un enlace",
  stopOpened: "Abrió el correo",
  stopCompanyReplied: "Otra persona de esta empresa respondió",
  stopBounced: "La dirección rebotó",
  stopUnsubscribed: "Canceló la suscripción",
  stopDoNotContact: "Marcado como No contactar",
  replyInterested: "Interesado",
  replyNotInterested: "No interesado",
  replyOutOfOffice: "Fuera de la oficina",
  replyWrongPerson: "Persona equivocada",

  blockerName: "Añade un nombre a la campaña",
  blockerSource: "Elige una fuente de leads",
  blockerAudience: "La fuente elegida no tiene correos a los que enviar",
  blockerSequence: "Añade al menos un paso de correo",
  blockerEmptyBody: "Algunos pasos de correo tienen el cuerpo vacío",
  blockerFirstSubject: "El primer correo necesita un asunto",
  blockerMailboxes: "Elige al menos un buzón de envío",
  blockerTimezone: "Define una zona horaria en Programación",
  blockerNoActiveDays: "Elige al menos un día activo de envío",
  warningNoTracking: "El seguimiento de aperturas y clics está desactivado, así que no verás la interacción",
  warningSingleMailbox: "Solo hay un buzón elegido. Añade más para mejorar la entrega",
  warningNoStartDate: "Sin fecha de inicio. La campaña empieza en cuanto la lances",
  warningLongDuration: "Llegar a toda la audiencia tardará mucho con la capacidad actual",
  warningReplyTracking:
    "Ningún buzón elegido tiene conectada la bandeja unificada, así que no se rastrearán las respuestas ni la detención al responder. Actívala en tus cuentas de correo.",
  warningStopOpenNeedsTracking:
    "Detienes al abrir, pero el seguimiento de aperturas está desactivado, así que la detención nunca se activará. Activa “Rastrear aperturas”.",
  warningStopClickNeedsTracking:
    "Detienes al hacer clic, pero el seguimiento de clics está desactivado, así que la detención nunca se activará. Activa “Rastrear clics”.",

  weekdayMonday: "Lun",
  weekdayTuesday: "Mar",
  weekdayWednesday: "Mié",
  weekdayThursday: "Jue",
  weekdayFriday: "Vie",
  weekdaySaturday: "Sáb",
  weekdaySunday: "Dom",

  smartCategorization: "Categorización inteligente con IA",
  disabled: "Desactivada",
  replyCategorization: "Categorización de respuestas",
  trackOpens: "Rastrear aperturas",
  trackClicks: "Rastrear clics",

  couldNotCreate: "No se pudo crear la campaña",
  upgradePlan: "Mejorar plan",
  backToCampaigns: "Volver a las campañas",
  creatingCampaign: "Creando tu campaña…",

  listDescription: "Crea, añade o elimina campañas.",
  manageEmailAccounts: "Administrar cuentas de correo",
  newCampaign: "Nueva campaña",
  showingResults: { one: "Mostrando 1 resultado", other: "Mostrando {count} resultados" },
  noCampaignsTitle: "Aún no hay campañas",
  noCampaignsDescription:
    "Escribe una secuencia, elige tus buzones y llega a los leads de un análisis o de un filtro.",
  columnTitle: "Título",
  columnStats: "Estadísticas",
  columnActions: "Acciones",
  actionsFor: "Acciones de {name}",
  viewStats: "Ver estadísticas",
  continueEditing: "Seguir editando",
  deleteTitle: "¿Eliminar {name}?",
  deleteDescription: "Sus leads se quedan en tus prospectos. Esta acción no se puede deshacer.",
  campaignDeleted: "Campaña eliminada",
  couldNotDelete: "No se pudo eliminar la campaña",
};

export const CAMPAIGN_CATALOGS: Catalogs<CampaignCatalog> = { en: EN, es: ES };

type Translator = Translate<CampaignCatalog>;

export const STATUS: Readonly<Record<CampaignStatus, { label: PlainKey<CampaignCatalog>; tone: Tone }>> = {
  draft: { label: "statusDraft", tone: "neutral" },
  "not-started": { label: "statusNotStarted", tone: "neutral" },
  running: { label: "statusRunning", tone: "info" },
  paused: { label: "statusPaused", tone: "warning" },
  completed: { label: "statusCompleted", tone: "success" },
  stopped: { label: "statusStopped", tone: "danger" },
};

export const PAUSE_REASON: Readonly<Record<PauseReason, PlainKey<CampaignCatalog>>> = {
  manual: "pauseManual",
  high_bounce: "pauseHighBounce",
  sender_invalid: "pauseSenderInvalid",
  no_senders: "pauseNoSenders",
};

export const LEAD_STATUS: Readonly<Record<LeadStatus, { label: PlainKey<CampaignCatalog>; tone: Tone }>> = {
  ACTIVE: { label: "leadActive", tone: "info" },
  WAITING: { label: "leadWaiting", tone: "neutral" },
  COMPLETED: { label: "leadCompleted", tone: "success" },
  STOPPED: { label: "leadStopped", tone: "warning" },
  BOUNCED: { label: "leadBounced", tone: "danger" },
  SKIPPED: { label: "leadSkipped", tone: "neutral" },
  REPLIED: { label: "leadReplied", tone: "success" },
};

export const STOP_REASON: Readonly<Record<StopReason, PlainKey<CampaignCatalog>>> = {
  replied: "stopReplied",
  clicked: "stopClicked",
  opened: "stopOpened",
  company_replied: "stopCompanyReplied",
  bounced: "stopBounced",
  unsubscribed: "stopUnsubscribed",
  do_not_contact: "stopDoNotContact",
};

export const REPLY_CATEGORY: Readonly<
  Record<ReplyCategory, { label: PlainKey<CampaignCatalog>; tone: Tone }>
> = {
  interested: { label: "replyInterested", tone: "success" },
  "not-interested": { label: "replyNotInterested", tone: "danger" },
  "out-of-office": { label: "replyOutOfOffice", tone: "neutral" },
  "wrong-person": { label: "replyWrongPerson", tone: "warning" },
};

export const BLOCKER: Readonly<Record<ReviewBlocker, PlainKey<CampaignCatalog>>> = {
  name: "blockerName",
  source: "blockerSource",
  audience: "blockerAudience",
  sequence: "blockerSequence",
  emptyBody: "blockerEmptyBody",
  firstSubject: "blockerFirstSubject",
  mailboxes: "blockerMailboxes",
  timezone: "blockerTimezone",
  noActiveDays: "blockerNoActiveDays",
};

export const WARNING: Readonly<Record<ReviewWarning, PlainKey<CampaignCatalog>>> = {
  noTracking: "warningNoTracking",
  singleMailbox: "warningSingleMailbox",
  noStartDate: "warningNoStartDate",
  longDuration: "warningLongDuration",
  replyTracking: "warningReplyTracking",
  stopOpenNeedsTracking: "warningStopOpenNeedsTracking",
  stopClickNeedsTracking: "warningStopClickNeedsTracking",
};

export const WEEKDAY_LABEL: Readonly<Record<Weekday, PlainKey<CampaignCatalog>>> = {
  monday: "weekdayMonday",
  tuesday: "weekdayTuesday",
  wednesday: "weekdayWednesday",
  thursday: "weekdayThursday",
  friday: "weekdayFriday",
  saturday: "weekdaySaturday",
  sunday: "weekdaySunday",
};

export const TIMEZONES = [
  "Europe/Madrid",
  "Europe/London",
  "America/New_York",
  "America/Mexico_City",
  "America/Sao_Paulo",
  "UTC",
] as const;

export function relativeDate(iso: string, t: Translator): string {
  const days = Math.round((Date.now() - Date.parse(iso)) / 86_400_000);
  if (days < 1) return t("createdToday");
  if (days < 30) return t("createdDaysAgo", { count: days });
  if (days < 365) return t("createdMonthsAgo", { count: Math.round(days / 30) });
  return t("createdYearsAgo", { count: days < 730 ? 1 : Math.floor(days / 365) });
}

export function percent(part: number, whole: number, format: Formats): string {
  const share = whole === 0 ? 0 : part / whole;
  return format.number(Math.round(share * 1000) / 1000, { style: "percent", maximumFractionDigits: 1 });
}
